/**
 * Standard Free-Margin gate helpers (MT-style).
 * Server remains source of truth; UI uses this for early feedback only.
 */

export function readFreeMargin(summary) {
  if (!summary || typeof summary !== "object") return null;
  const free = Number(summary.freeMargin ?? summary.FreeMargin);
  return Number.isFinite(free) ? free : null;
}

export function readEquity(summary) {
  if (!summary || typeof summary !== "object") return null;
  const equity = Number(summary.equity ?? summary.Equity);
  return Number.isFinite(equity) ? equity : null;
}

/**
 * True when this order side reduces/closes an existing open position for the script.
 * Closing is always allowed even when Free Margin <= 0.
 */
export function isReducingOpenPosition(positions, liveRow, side) {
  if (!liveRow || !side) return false;
  const script = String(
    liveRow.script || liveRow.displayName || liveRow.Script || liveRow.DisplayName || ""
  )
    .trim()
    .toUpperCase();
  const display = String(liveRow.displayName || liveRow.DisplayName || "").trim().toUpperCase();
  if (!script && !display) return false;

  const list = Array.isArray(positions)
    ? positions
    : positions?.values
      ? [...positions.values()]
      : [];

  const sideBuy = String(side).toUpperCase() === "BUY";
  for (const row of list) {
    const rowScript = String(row.script || row.displayName || "").trim().toUpperCase();
    const rowDisplay = String(row.displayName || "").trim().toUpperCase();
    const match =
      (script && (rowScript === script || rowDisplay === script)) ||
      (display && (rowScript === display || rowDisplay === display));
    if (!match) continue;
    const netQty = Number(row.netQty ?? row.qty ?? 0);
    if (!Number.isFinite(netQty) || netQty === 0) continue;
    // Buy reduces short; Sell reduces long.
    if (sideBuy && netQty < 0) return true;
    if (!sideBuy && netQty > 0) return true;
  }
  return false;
}

/**
 * Early client gate. Returns error message or null.
 * - Closing/reducing: always allow
 * - New exposure with FreeMargin <= 0: block
 */
export function clientFreeMarginMessage({ summary, positions, liveRow, side }) {
  if (isReducingOpenPosition(positions, liveRow, side)) return null;

  const free = readFreeMargin(summary);
  if (free == null) return null; // summary not loaded yet — server will enforce

  if (free <= 0) {
    return `Insufficient free margin. Free: ${free.toFixed(2)}. Deposit or credit required to open a new position.`;
  }
  return null;
}
